import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useT } from '../utils/translate';

// Vazifalar bo'limi uchun amoCRM uslubidagi Kun/Hafta/Oy kalendar ko'rinishi —
// mavjud Kanban boarddagi bir xil `tasks` ro'yxatini dueDate bo'yicha
// kunlarga taqsimlab ko'rsatadi. Yangi sana kutubxonasi ishlatilmaydi —
// TasksPage.jsx/DateTimePicker.jsx'dagi kabi native Date bilan.
const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const mondayOffset = (d) => (d.getDay() + 6) % 7; // Mon=0

function isOverdue(dueDate) {
  return !!dueDate && new Date(dueDate) < new Date();
}

function TaskChip({ task, onView }) {
  const overdue = isOverdue(task.dueDate);
  return (
    <button onClick={() => onView(task)} title={task.title}
      className={`w-full text-left text-[11px] font-medium px-1.5 py-0.5 rounded-md truncate transition-colors ${
        overdue ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
      }`}>
      {task.dueDate && <span className="font-mono mr-1">{pad(new Date(task.dueDate).getHours())}:{pad(new Date(task.dueDate).getMinutes())}</span>}
      {task.title}
    </button>
  );
}

function DayCell({ date, tasks, onView, compact }) {
  const today = startOfDay(new Date()).getTime() === startOfDay(date).getTime();
  const shown = compact ? tasks.slice(0, 3) : tasks;
  const rest  = compact ? tasks.length - shown.length : 0;
  return (
    <div className={`min-h-24 flex flex-col gap-1 p-1.5 rounded-lg border ${today ? 'border-primary-300 bg-primary-50/30' : 'border-surface-100'}`}>
      <span className={`text-[11px] font-semibold ${today ? 'text-primary-600' : 'text-ink-tertiary'}`}>{date.getDate()}</span>
      <div className="flex flex-col gap-0.5 flex-1 min-h-0 overflow-hidden">
        {shown.map(task => <TaskChip key={task._id} task={task} onView={onView} />)}
        {rest > 0 && <span className="text-[10px] text-ink-disabled px-1.5">+{rest}</span>}
      </div>
    </div>
  );
}

export default function TasksCalendarView({ tasks, onView }) {
  const t = useT();
  const [mode, setMode] = useState('month'); // 'day' | 'week' | 'month'
  const [cursor, setCursor] = useState(() => new Date());

  const byDay = useMemo(() => {
    const map = new Map();
    for (const task of tasks) {
      if (!task.dueDate) continue;
      const key = dayKey(new Date(task.dueDate));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(task);
    }
    for (const list of map.values()) list.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    return map;
  }, [tasks]);

  const noDateTasks = useMemo(() => tasks.filter(task => !task.dueDate), [tasks]);

  const weekdayLabels = [...Array(7)].map((_, i) => new Date(2024, 0, 1 + i).toLocaleDateString('uz-UZ', { weekday: 'short' }));

  const shiftCursor = (dir) => {
    setCursor(prev => {
      const next = new Date(prev);
      if (mode === 'day') next.setDate(prev.getDate() + dir);
      else if (mode === 'week') next.setDate(prev.getDate() + dir * 7);
      else next.setMonth(prev.getMonth() + dir);
      return next;
    });
  };

  let header = '';
  let body = null;

  if (mode === 'month') {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lead = mondayOffset(first);
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    header = first.toLocaleDateString('uz-UZ', { month: 'long', year: 'numeric' });
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    body = (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekdayLabels.map((w, i) => <div key={i} className="text-center text-[10px] font-medium text-ink-tertiary uppercase py-1">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((date, i) => date
            ? <DayCell key={i} date={date} tasks={byDay.get(dayKey(date)) || []} onView={onView} compact />
            : <div key={i} />)}
        </div>
      </div>
    );
  } else if (mode === 'week') {
    const monday = new Date(cursor);
    monday.setDate(cursor.getDate() - mondayOffset(cursor));
    const days = [...Array(7)].map((_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
    header = `${days[0].toLocaleDateString('uz-UZ')} — ${days[6].toLocaleDateString('uz-UZ')}`;
    body = (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {days.map((d, i) => (
            <div key={i} className="text-center text-[10px] font-medium text-ink-tertiary uppercase py-1">
              {weekdayLabels[i]} <span className="text-ink">{d.getDate()}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((date, i) => <DayCell key={i} date={date} tasks={byDay.get(dayKey(date)) || []} onView={onView} />)}
        </div>
      </div>
    );
  } else {
    header = cursor.toLocaleDateString('uz-UZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const dayTasks = byDay.get(dayKey(cursor)) || [];
    body = (
      <div className="space-y-1.5 max-w-md">
        {dayTasks.length === 0 && <p className="text-sm text-ink-tertiary py-6 text-center">{t('tasks.noStages')}</p>}
        {dayTasks.map(task => (
          <button key={task._id} onClick={() => onView(task)}
            className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-xl border transition-colors ${
              isOverdue(task.dueDate) ? 'border-red-200 bg-red-50/40' : 'border-surface-200 hover:border-primary-300'
            }`}>
            <Clock className="w-3.5 h-3.5 text-ink-tertiary shrink-0" />
            <span className="font-mono text-xs text-ink-tertiary shrink-0">{pad(new Date(task.dueDate).getHours())}:{pad(new Date(task.dueDate).getMinutes())}</span>
            <span className="text-sm text-ink truncate">{task.title}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full overflow-y-auto p-1">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 bg-surface-50 border border-surface-200 rounded-xl p-1">
          {[['day', t('tasks.viewDay')], ['week', t('tasks.viewWeek')], ['month', t('tasks.viewMonth')]].map(([key, label]) => (
            <button key={key} onClick={() => setMode(key)}
              className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors ${mode === key ? 'bg-white text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftCursor(-1)} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold text-ink capitalize min-w-[140px] text-center">{header}</span>
          <button onClick={() => shiftCursor(1)} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {body}

      {noDateTasks.length > 0 && (
        <div className="border-t border-surface-100 pt-2 mt-2">
          <p className="text-[11px] font-medium text-ink-tertiary mb-1.5">{t('tasks.noDateBucket')} ({noDateTasks.length})</p>
          <div className="flex flex-wrap gap-1.5">
            {noDateTasks.map(task => (
              <button key={task._id} onClick={() => onView(task)}
                className="text-[11px] font-medium px-2 py-1 rounded-lg bg-surface-100 text-ink-secondary hover:bg-surface-200 transition-colors truncate max-w-[220px]">
                {task.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
